/**
 * Mock Journey Response v2.0 for UI Development
 *
 * Toggle USE_MOCK = true to test UI without burning API credits.
 * This mock matches the loadTestData() profile in useAppStore.ts
 *
 * Test Data Profile:
 * - Name: Test User
 * - Age: 35, Target Retirement: 45
 * - US Assets: $1.31M total (401k: $500K, Roth: $150K, Trad IRA: $100K, Brokerage: $300K, etc.)
 * - India Assets: ₹58L
 * - RSUs: 600 unvested shares @ $150 = $90K
 * - Years in USA: 10 (Social Security eligible with 40 credits)
 *
 * Usage:
 * 1. Set USE_MOCK = true below
 * 2. Run the app: npm run dev
 * 3. Press Ctrl+Shift+T to load test data
 * 4. Open Journey Modal - it will show this mock data
 * 5. Set USE_MOCK = false when done testing
 */

import type { JourneyOutput } from '@/lib/ai/schemas/journeyOutput.schema';

// ============================================
// Feature Flags for Journey Generation
// ============================================

// USE_MOCK: Returns hardcoded mock data (for UI development)
export const USE_MOCK = false; // Set to true for testing, false for production

// USE_RULE_ENGINE_ONLY: Skip LLM API, use Rule Engine directly (for cost savings)
// When true: Instant response, $0 cost, uses generateFallbackJourney()
// When false: Calls LLM API (~$0.05-0.10, 20-50s latency)
export const USE_RULE_ENGINE_ONLY = true; // ← SET TO TRUE FOR BETA LAUNCH

// ============================================
// v2.0 Mock Journey Response (matches loadTestData)
// ============================================
const mockJourney: JourneyOutput = {
  schemaVersion: '2.0',
  generatedAt: new Date().toISOString(),

  // ============================================
  // v2.0: Hero Stats (The "Wow" Moment)
  // ============================================
  heroStats: {
    totalTaxSavings: {
      amountINR: 5850000, // ₹58.5 Lakhs
      formatted: '₹58.5L',
      comparedTo: 'via RSU timing + RNOR benefits',
    },
    timeToFIRE: {
      months: 24,
      formatted: '2 years',
      status: 'on_track',
    },
    confidence: {
      score: 88,
      level: 'high',
      explanation: 'Complete financial data with diversified US & India assets',
    },
    rnorBenefit: {
      eligible: true,
      years: 2,
      impactDescription: 'Foreign income tax-free during RNOR window',
    },
    // Social Security - Test User has 10 years in USA (40 credits = eligible)
    ssEligibility: {
      eligible: true,
      yearsToQualify: 0,
      lifetimeValueFormatted: '$180K-$350K',
      recommendation: 'You qualify for Social Security! Consider waiting until 67 for full benefits.',
      isHighValueOpportunity: true,
    },
    personalizedHeadline: 'Test User, your FIRE journey is on track! 🎯',
    personalizedSubheadline: 'With $1.31M in US assets and strategic RNOR timing, you could save ₹58.5L in taxes while achieving FIRE in 2 years.',
  },

  // ============================================
  // v2.0: Tax Savings Breakdown
  // ============================================
  taxSavingsHighlight: {
    totalSavingsINR: 5850000,
    totalFormatted: '₹58.5L',
    savingsBreakdown: [
      {
        category: 'RSU Liquidation Timing',
        amountINR: 1125000,
        formatted: '₹11.25L',
        description: 'Selling $90K unvested RSUs (600 shares @ $150) before becoming ROR saves 5% differential between US LTCG (15%) and India LTCG (20%).',
        timing: 'Before March 2027',
        confidence: 'high',
      },
      {
        category: 'Roth IRA Strategy',
        amountINR: 1875000,
        formatted: '₹18.75L',
        description: 'Your $150K Roth IRA qualified distributions are tax-free. Converting additional $50K from Traditional IRA to Roth before return locks in lower US rates.',
        timing: 'Years 1-2 before return',
        confidence: 'high',
      },
      {
        category: '401(k) Conversion',
        amountINR: 1560000,
        formatted: '₹15.6L',
        description: 'Converting $75K/year from your $500K 401(k) to Roth at US tax rates (24%) vs. Indian ROR rates (30%+).',
        timing: 'Before returning',
        confidence: 'high',
      },
      {
        category: 'Brokerage Capital Gains',
        amountINR: 750000,
        formatted: '₹7.5L',
        description: 'Tax-loss harvesting and realizing LTCG on your $300K brokerage at US 15% rate instead of India 20%.',
        timing: 'Q4 2026',
        confidence: 'medium',
      },
      {
        category: 'DTAA Benefits',
        amountINR: 540000,
        formatted: '₹5.4L',
        description: 'US-India treaty prevents double taxation on retirement account withdrawals.',
        timing: 'During withdrawals',
        confidence: 'medium',
      },
    ],
    methodology: 'Based on $1.31M US assets, 10 years US work history, current DTAA provisions, 30% India ROR bracket vs. 15-24% US rates. Exchange rate: ₹83.5/$.',
    disclaimer: 'Estimates based on current tax laws. Actual savings depend on return date, exchange rates, and regulatory changes. Consult a CA.',
  },

  // ============================================
  // v2.0: AI Insights (Expert Observations)
  // ============================================
  aiInsights: [
    {
      id: 'insight-1',
      type: 'opportunity',
      title: 'Your 401(k) is your biggest tax optimization lever',
      insight: 'With $500K in your 401(k), converting to Roth IRA before returning could save significant taxes. In the US, you\'re in a lower bracket than you\'ll face as an Indian ROR. Consider converting $75K/year over the next 2 years.',
      specificAmount: '₹15-20L potential savings',
      relatedAsset: '401(k) - $500,000',
      actionRequired: true,
      urgency: 'before_return',
      relatedActionId: 'action-roth-conversion',
    },
    {
      id: 'insight-2',
      type: 'warning',
      title: 'RSU vesting creates tax complexity',
      insight: 'Your 600 unvested RSUs ($90K at current price) will create US-sourced income even after you become Indian resident. Consider negotiating accelerated vesting or selling immediately upon vest.',
      specificAmount: '$90K affected',
      relatedAsset: 'Tech Corp RSUs - 600 unvested shares',
      actionRequired: true,
      urgency: 'before_return',
    },
    {
      id: 'insight-3',
      type: 'confirmation',
      title: 'Social Security locked in',
      insight: 'With 10 years of US work history, you\'ve earned the required 40 credits for Social Security benefits. Expected lifetime value: $180K-$350K starting at age 62-67. This is protected even after moving to India.',
      specificAmount: '$180K-$350K lifetime',
      actionRequired: false,
      urgency: 'ongoing',
    },
    {
      id: 'insight-4',
      type: 'tip',
      title: 'Strong India asset base accelerates FIRE',
      insight: 'Your ₹58L in India assets (MF: ₹20L, EPF: ₹10L, Stocks: ₹5L, FDs: ₹10L) provides rupee stability. This reduces currency risk and gives you a solid foundation on arrival.',
      actionRequired: false,
      urgency: 'ongoing',
    },
    {
      id: 'insight-5',
      type: 'critical',
      title: 'HSA requires special handling',
      insight: 'Your $25K HSA loses tax-free status in India. Use it for qualified medical expenses before returning, or factor in the tax hit when using it post-return.',
      specificAmount: '$25K HSA balance',
      relatedAsset: 'HSA Account',
      actionRequired: true,
      urgency: 'before_return',
    },
  ],

  // ============================================
  // v2.0: Liquidation Sequence
  // ============================================
  liquidationSequence: [
    {
      id: 'liq-1',
      sequence: 1,
      asset: 'RSUs (Tech Corp)',
      action: 'Sell vested shares as they vest',
      timing: 'before_move',
      timingDescription: 'Sell within 30 days of each vest date through Q4 2026',
      reason: 'Lock in 15% US LTCG rate vs 20%+ India LTCG. Avoid cross-border tax complexity.',
      estimatedValueUSD: 60000,
      estimatedValueINR: 5010000,
      taxImplication: 'US LTCG at 15% (vs. 20%+ as India ROR)',
      requiresCAReview: true,
    },
    {
      id: 'liq-2',
      sequence: 2,
      asset: 'ESPP Shares',
      action: 'Sell after qualifying disposition',
      timing: 'before_move',
      timingDescription: 'Sell ESPP shares that have met 2-year/1-year holding requirements',
      reason: 'Discount taxed as US capital gains (15%) vs India income (30%+).',
      estimatedValueUSD: 75000,
      estimatedValueINR: 6262500,
      taxImplication: 'Qualifying disposition: discount taxed as LTCG',
      requiresCAReview: true,
    },
    {
      id: 'liq-3',
      sequence: 3,
      asset: 'Taxable Brokerage',
      action: 'Tax-loss harvest and realize LTCG',
      timing: 'before_move',
      timingDescription: 'Q4 2026 - before calendar year end',
      reason: 'Lock in US 15% LTCG rate. Reset cost basis. Harvest losses to offset gains.',
      estimatedValueUSD: 300000,
      estimatedValueINR: 25050000,
      taxImplication: 'US LTCG at 15%, harvest losses to offset',
      requiresCAReview: true,
    },
    {
      id: 'liq-4',
      sequence: 4,
      asset: 'HSA Account',
      action: 'Use for qualified medical expenses',
      timing: 'before_move',
      timingDescription: 'Before departure - schedule medical procedures',
      reason: 'HSA loses triple tax advantage in India. Use tax-free now for dental, vision, medical.',
      estimatedValueUSD: 25000,
      estimatedValueINR: 2087500,
      taxImplication: 'Tax-free if used for qualified expenses',
      requiresCAReview: false,
    },
    {
      id: 'liq-5',
      sequence: 5,
      asset: 'Crypto Holdings',
      action: 'Evaluate and potentially liquidate',
      timing: 'before_move',
      timingDescription: 'Q1 2027 - before return',
      reason: 'India taxes crypto at 30% flat rate with no loss offset. US allows loss harvesting.',
      estimatedValueUSD: 50000,
      estimatedValueINR: 4175000,
      taxImplication: 'US: variable rates with loss offset. India: 30% flat, no offset',
      requiresCAReview: true,
    },
    {
      id: 'liq-6',
      sequence: 6,
      asset: 'Traditional IRA → Roth Conversion',
      action: 'Convert $50K from Traditional to Roth IRA',
      timing: 'during_rnor',
      timingDescription: 'Year 1-2 after return during RNOR period',
      reason: 'Foreign income (including Roth growth) tax-free during RNOR. Lock in conversion at known rates.',
      estimatedValueUSD: 50000,
      estimatedValueINR: 4175000,
      taxImplication: 'US tax on conversion, then tax-free growth',
      requiresCAReview: true,
    },
    {
      id: 'liq-7',
      sequence: 7,
      asset: '401(k) Partial Rollover',
      action: 'Roll $75K to Roth IRA annually',
      timing: 'during_rnor',
      timingDescription: 'Convert $75K/year during RNOR (2 years)',
      reason: 'US tax bracket (24%) lower than India ROR bracket (30%+). Roth grows tax-free.',
      estimatedValueUSD: 150000,
      estimatedValueINR: 12525000,
      taxImplication: 'Pay US tax now, avoid higher India tax later',
      requiresCAReview: true,
    },
    {
      id: 'liq-8',
      sequence: 8,
      asset: 'Remaining 401(k)/IRA',
      action: 'Maintain and withdraw strategically',
      timing: 'post_rnor',
      timingDescription: 'Post age 59½ for penalty-free withdrawals',
      reason: 'DTAA prevents double taxation. Withdraw when in lower tax bracket.',
      estimatedValueUSD: 400000,
      estimatedValueINR: 33400000,
      taxImplication: 'DTAA Article 20 applies - claim foreign tax credit',
      requiresCAReview: true,
    },
  ],

  // ============================================
  // v2.0: Scenario Comparison
  // ============================================
  scenarioComparison: {
    scenarios: [
      {
        id: 'scenario-early',
        name: 'Early Return',
        returnDate: 'July 2026',
        returnFY: '2026-27',
        isRecommended: false,
        isCurrentPlan: false,
        metrics: {
          rnorYears: 2,
          taxSavingsINR: 4200000,
          taxSavingsFormatted: '₹42L',
          additionalCorpusINR: 0,
          additionalCorpusFormatted: '₹0',
          riskLevel: 'low',
          riskExplanation: 'Less time to optimize but lower execution risk',
        },
        pros: [
          'Start RNOR clock sooner',
          'Lower market risk',
          'Reunite with family faster',
        ],
        cons: [
          'Less time for Roth conversion',
          'RSUs still unvested',
          'Miss 2026 bonus cycle',
        ],
      },
      {
        id: 'scenario-optimal',
        name: 'Optimal Timing',
        returnDate: 'March 2027',
        returnFY: '2026-27',
        isRecommended: true,
        isCurrentPlan: true,
        metrics: {
          rnorYears: 2,
          taxSavingsINR: 5850000,
          taxSavingsFormatted: '₹58.5L',
          additionalCorpusINR: 2500000,
          additionalCorpusFormatted: '₹25L',
          riskLevel: 'medium',
          riskExplanation: 'Balanced approach with good tax optimization window',
        },
        pros: [
          'Maximize Roth conversions',
          'RSUs fully vested by Q4 2026',
          'Capture 2026 bonus + RSU vest',
        ],
        cons: [
          '9 more months abroad',
          'Market risk on $1.31M portfolio',
          'Policy change risk',
        ],
      },
      {
        id: 'scenario-delayed',
        name: 'Extended Stay',
        returnDate: 'January 2028',
        returnFY: '2027-28',
        isRecommended: false,
        isCurrentPlan: false,
        metrics: {
          rnorYears: 2,
          taxSavingsINR: 6500000,
          taxSavingsFormatted: '₹65L',
          additionalCorpusINR: 5000000,
          additionalCorpusFormatted: '₹50L',
          riskLevel: 'high',
          riskExplanation: 'Higher returns but significant lifestyle and regulatory risks',
        },
        pros: [
          'Additional savings year',
          'Complete all Roth conversions',
          'Larger corpus ($1.4M+)',
        ],
        cons: [
          '18+ months more abroad',
          'Tax law change risk',
          'Parents getting older',
        ],
      },
    ],
    recommendedScenarioId: 'scenario-optimal',
    recommendationRationale: 'March 2027 balances tax optimization (₹58.5L savings) with personal timeline. Allows completing RSU liquidation and significant Roth conversion while not overextending stay.',
    keyTradeoff: 'Each 6-month delay adds ~₹8L in savings but increases regulatory risk and time away from family. The "Optimal" scenario captures 90% of maximum savings with manageable risk.',
  },

  // ============================================
  // Core Fields
  // ============================================
  summary: {
    headline: 'Your 24-Month Journey to FIRE in India',
    subheadline: 'Strategic plan to maximize RNOR benefits, optimize $1.31M US assets, and achieve financial independence by age 37',
    confidenceLevel: 'high',
    confidenceExplanation: 'Complete financial data with diversified assets across US and India.',
  },

  timeline: {
    startDate: 'January 2026',
    targetReturnDate: 'March 2027',
    targetReturnFY: 'FY 2026-27',
    rnorEndDate: 'March 2029',
    fireTargetDate: 'March 2028',
    totalMonths: 24,
    milestones: [
      { id: 'ms-1', date: 'Jan 2026', label: 'Start Preparation', type: 'start', description: 'Begin RSU liquidation and Roth conversion strategy' },
      { id: 'ms-2', date: 'Mar 2027', label: 'Return to India', type: 'return', description: 'Relocate to Bangalore and establish residency' },
      { id: 'ms-3', date: 'Mar 2028', label: 'FIRE Target', type: 'fire_target', description: 'Achieve financial independence' },
      { id: 'ms-4', date: 'Mar 2029', label: 'RNOR Ends', type: 'rnor_end', description: 'Transition to ROR status' },
    ],
  },

  phases: [
    {
      id: 'phase-1',
      sequence: 1,
      name: 'Pre-Return Optimization',
      dateRange: { start: 'Jan 2026', end: 'Mar 2027' },
      description: 'Maximize tax efficiency before becoming Indian tax resident. Focus on RSU liquidation, Roth conversions, and asset restructuring.',
      status: 'current',
      totalActions: 6,
      criticalActions: 3,
      estimatedSavingsINR: 3500000,
      actions: [
        {
          id: 'action-rsu-liquidation',
          sequence: 1,
          title: 'Execute RSU Liquidation Strategy',
          description: 'Sell Tech Corp RSUs as they vest (600 shares @ $150 = $90K) to lock in US LTCG rates.',
          rationale: 'US LTCG (15%) vs India LTCG (20%+). Also simplifies cross-border tax filing.',
          category: 'investment',
          priority: 'critical',
          suggestedTiming: 'As each tranche vests',
          deadline: 'December 2026',
          isTimeSensitive: true,
          estimatedSavingsINR: 1125000,
          estimatedSavingsFormatted: '₹11.25L',
          impactDescription: '5% rate differential on $90K RSU value',
          requiresCAReview: true,
          caReviewReason: 'Complex RSU taxation across jurisdictions',
          sourceReference: 'IRC Section 83, India LTCG rules',
          factPackSource: 'complex_assets',
        },
        {
          id: 'action-roth-conversion',
          sequence: 2,
          title: 'Execute Roth IRA Conversion Strategy',
          description: 'Convert $75K/year from 401(k) to Roth IRA to lock in US tax rates before becoming Indian ROR.',
          rationale: 'US tax bracket (24%) is lower than Indian ROR bracket (30%+). Roth growth is tax-free.',
          category: 'tax',
          priority: 'critical',
          suggestedTiming: 'Q1 2026 and Q1 2027',
          deadline: 'Before return',
          isTimeSensitive: true,
          estimatedSavingsINR: 1560000,
          estimatedSavingsFormatted: '₹15.6L',
          impactDescription: '6% rate differential on $150K converted',
          requiresCAReview: true,
          caReviewReason: 'DTAA implications on Roth IRA',
          sourceReference: 'DTAA Article 20, IRS Publication 590-A',
          factPackSource: 'retirement_accounts',
        },
        {
          id: 'action-espp-liquidation',
          sequence: 3,
          title: 'Liquidate ESPP Holdings',
          description: 'Sell $75K ESPP shares that have met qualifying disposition requirements.',
          rationale: 'Qualifying ESPP: discount taxed as LTCG (15%) vs ordinary income (30%+) in India.',
          category: 'investment',
          priority: 'high',
          suggestedTiming: 'Q3 2026',
          isTimeSensitive: true,
          estimatedSavingsINR: 468000,
          estimatedSavingsFormatted: '₹4.68L',
          impactDescription: 'Avoid 15%+ tax on ESPP discount portion',
          requiresCAReview: true,
          caReviewReason: 'ESPP disposition rules',
          factPackSource: 'complex_assets',
        },
        {
          id: 'action-brokerage-harvest',
          sequence: 4,
          title: 'Tax-Loss Harvest Brokerage Account',
          description: 'Review $300K brokerage for tax-loss harvesting and LTCG realization.',
          rationale: 'Lock in US 15% LTCG rate. Reset cost basis. Offset gains with harvested losses.',
          category: 'investment',
          priority: 'high',
          suggestedTiming: 'Q4 2026',
          isTimeSensitive: true,
          estimatedSavingsINR: 750000,
          estimatedSavingsFormatted: '₹7.5L',
          impactDescription: '5% rate differential + loss harvesting benefits',
          requiresCAReview: true,
          caReviewReason: 'Wash sale rules',
          factPackSource: 'investments',
        },
        {
          id: 'action-hsa-usage',
          sequence: 5,
          title: 'Maximize HSA Usage',
          description: 'Use $25K HSA for qualified medical expenses before return. Schedule dental, vision, elective procedures.',
          rationale: 'HSA loses triple tax advantage in India. Tax-free usage only in US.',
          category: 'account',
          priority: 'medium',
          suggestedTiming: 'Throughout 2026',
          isTimeSensitive: true,
          requiresCAReview: false,
          factPackSource: 'retirement_accounts',
        },
        {
          id: 'action-documents',
          sequence: 6,
          title: 'Gather Tax Residency Documentation',
          description: 'Collect documents proving NRI status: US tax returns, W-2s, utility bills, bank statements.',
          rationale: 'Need to prove NRI status to claim RNOR benefits and DTAA credits.',
          category: 'documentation',
          priority: 'medium',
          suggestedTiming: 'Q1 2027',
          isTimeSensitive: false,
          requiresCAReview: false,
          factPackSource: 'profile',
        },
      ],
    },
    {
      id: 'phase-2',
      sequence: 2,
      name: 'RNOR Period (Year 1)',
      dateRange: { start: 'Apr 2027', end: 'Mar 2028' },
      description: 'First year as RNOR. Foreign income tax-free. Focus on settling in Bangalore and continuing Roth conversions.',
      status: 'upcoming',
      totalActions: 4,
      criticalActions: 1,
      estimatedSavingsINR: 1500000,
      actions: [
        {
          id: 'action-rnor-income',
          sequence: 1,
          title: 'Structure Foreign Income Receipts',
          description: 'Ensure dividends, capital gains from US accounts stay in US. Don\'t repatriate during RNOR.',
          rationale: 'Foreign income received abroad is tax-free for RNOR. Keep it outside India.',
          category: 'tax',
          priority: 'critical',
          suggestedTiming: 'Ongoing',
          isTimeSensitive: true,
          estimatedSavingsINR: 1500000,
          estimatedSavingsFormatted: '₹15L/year',
          impactDescription: 'Avoid 30% tax on foreign income',
          requiresCAReview: true,
          caReviewReason: 'RNOR rules are nuanced',
          factPackSource: 'income',
        },
        {
          id: 'action-bangalore-setup',
          sequence: 2,
          title: 'Establish Bangalore Residence',
          description: 'Rent 3BHK in target area. Budget: ₹60-80K/month based on comfortable lifestyle selection.',
          rationale: 'Bangalore selected as primary city. 18% lower cost than Mumbai with similar opportunities.',
          category: 'lifestyle',
          priority: 'high',
          suggestedTiming: 'Apr 2027',
          isTimeSensitive: false,
          requiresCAReview: false,
          factPackSource: 'lifestyle',
        },
        {
          id: 'action-invest-india',
          sequence: 3,
          title: 'Set Up Indian Investment Portfolio',
          description: 'Open demat account. Add to existing ₹58L India assets with systematic investments.',
          rationale: 'You already have ₹58L in India. Continue building rupee corpus for FIRE.',
          category: 'investment',
          priority: 'high',
          suggestedTiming: 'Q2 2027',
          isTimeSensitive: false,
          requiresCAReview: false,
          factPackSource: 'investments',
        },
        {
          id: 'action-advance-tax',
          sequence: 4,
          title: 'Set Up Advance Tax Payments',
          description: 'Register for quarterly advance tax on Indian-sourced income (rental, if any).',
          rationale: 'Required for Indian income. Penalties for late payment.',
          category: 'compliance',
          priority: 'medium',
          suggestedTiming: 'June 2027',
          isTimeSensitive: true,
          requiresCAReview: true,
          caReviewReason: 'First year filing can be complex',
          factPackSource: 'compliance',
        },
      ],
    },
    {
      id: 'phase-3',
      sequence: 3,
      name: 'FIRE Achievement',
      dateRange: { start: 'Apr 2028', end: 'Mar 2029' },
      description: 'Achieve FIRE target. Last RNOR year. Finalize corpus and begin sustainable withdrawal strategy.',
      status: 'future',
      totalActions: 3,
      criticalActions: 1,
      estimatedSavingsINR: 850000,
      actions: [
        {
          id: 'action-fire-validation',
          sequence: 1,
          title: 'FIRE Readiness Validation',
          description: 'Validate corpus against ₹3.2Cr target. Confirm 4% SWR sustainability with goals.',
          rationale: 'Final checkpoint. Your goals (₹25L renovation, travel, parents care) are factored in.',
          category: 'investment',
          priority: 'critical',
          suggestedTiming: 'Q1 2028',
          isTimeSensitive: false,
          requiresCAReview: false,
          factPackSource: 'fire_targets',
        },
        {
          id: 'action-final-rnor',
          sequence: 2,
          title: 'Final RNOR Optimization Review',
          description: 'Review all foreign income sources. Ensure proper documentation for RNOR claims.',
          rationale: 'Last year of RNOR benefits. Maximize remaining tax-free foreign income.',
          category: 'tax',
          priority: 'high',
          suggestedTiming: 'Q4 2028',
          isTimeSensitive: true,
          estimatedSavingsINR: 850000,
          estimatedSavingsFormatted: '₹8.5L',
          impactDescription: 'Final year of tax-free foreign income',
          requiresCAReview: true,
          caReviewReason: 'RNOR status verification',
          factPackSource: 'income',
        },
        {
          id: 'action-ror-prep',
          sequence: 3,
          title: 'Prepare for ROR Transition',
          description: 'Understand full Indian tax obligations. All global income becomes taxable from FY 2029-30.',
          rationale: 'Significant change in tax treatment. Plan withdrawal strategy accordingly.',
          category: 'compliance',
          priority: 'high',
          suggestedTiming: 'Q1 2029',
          isTimeSensitive: true,
          requiresCAReview: true,
          caReviewReason: 'Major status change',
          factPackSource: 'compliance',
        },
      ],
    },
  ],

  keyDecisionPoints: [
    {
      id: 'decision-1',
      title: 'Roth Conversion Amount',
      description: 'How much to convert from 401(k) to Roth each year? More = more tax now, less tax later.',
      suggestedDecisionDate: 'February 2026',
      options: ['$50K/year (conservative)', '$75K/year (recommended)', '$100K/year (aggressive)'],
      requiresProfessionalReview: true,
      reviewType: 'tax_attorney',
      factPackSource: 'retirement_accounts',
      impactIfDelayed: 'Each month delay reduces conversion window',
    },
    {
      id: 'decision-2',
      title: 'RSU Liquidation vs. Hold',
      description: 'Sell RSUs immediately on vest or hold for potential appreciation?',
      suggestedDecisionDate: 'December 2025',
      options: ['Sell on vest (recommended)', 'Hold 50%, sell 50%', 'Hold all'],
      requiresProfessionalReview: true,
      reviewType: 'financial_advisor',
      factPackSource: 'complex_assets',
      impactIfDelayed: 'Cross-border tax complexity increases',
    },
    {
      id: 'decision-3',
      title: 'Return Date Finalization',
      description: 'Exact return date affects FY classification and RNOR start.',
      suggestedDecisionDate: 'October 2026',
      options: ['Before March 31, 2027 (FY 26-27 - recommended)', 'After April 1, 2027 (FY 27-28)'],
      requiresProfessionalReview: true,
      reviewType: 'ca',
      factPackSource: 'profile',
      impactIfDelayed: 'May miss optimal RNOR timing',
    },
  ],

  assumptions: [
    { category: 'Exchange Rate', assumption: '₹83.5/USD (from profile)', source: 'user_input' },
    { category: 'Inflation (India)', assumption: '6% general, 10% medical', source: 'system_default' },
    { category: 'Investment Returns', assumption: '12% pre-retirement, 7% post-retirement', source: 'user_input' },
    { category: 'Tax Bracket (US)', assumption: '24% federal (married filing jointly)', source: 'calculated' },
    { category: 'Tax Bracket (India)', assumption: '30% (ROR), 0% foreign income (RNOR)', source: 'calculated' },
    { category: 'SWR', assumption: '4% Safe Withdrawal Rate', source: 'user_input' },
    { category: 'RNOR Period', assumption: '2 years based on NRI status', source: 'calculated' },
    { category: 'Social Security', assumption: 'Eligible (10 years / 40 credits)', source: 'calculated' },
  ],

  disclaimers: {
    standard: 'This AI-generated roadmap is for informational purposes only. It does not constitute financial, tax, or legal advice. Tax laws change frequently. Always consult qualified professionals before making financial decisions.',
    itemsRequiringCAReview: [
      'RSU liquidation timing and cross-border taxation',
      'Roth IRA conversion strategy and DTAA implications',
      'RNOR status eligibility verification',
      'ESPP disposition and discount taxation',
      '401(k)/IRA withdrawal strategy post-return',
    ],
    dataLimitations: [
      'Tax rates based on current laws (Jan 2026)',
      'RSU price assumed constant at $150/share',
      'Exchange rate assumed stable for projections',
      'Social Security estimates based on current benefit formula',
    ],
  },

  caveats: [
    'RNOR eligibility depends on your specific residency history',
    'RSU vest schedule may change based on company policy',
    'Social Security benefits subject to WEP/GPO if applicable',
    'Tax laws may change before your return date',
  ],
};

export const MOCK_JOURNEY_RESPONSE = {
  success: true,
  journey: mockJourney,
  timing: {
    factPackMs: 52,
    clarifierMs: 0,
    plannerMs: 9200,
    validationMs: 145,
    totalMs: 9397,
  },
  warnings: [],
};

// Helper to set mock wizard data for testing dashboard directly
export function setMockWizardData(): void {
  if (typeof window !== 'undefined' && USE_MOCK) {
    sessionStorage.setItem('nri_fire_wizard_complete', 'true');
  }
}
